import {
  BillingFrequency,
  BillingStatus,
  EntitlementDetails,
  InvoiceGroup,
  LinkedDestinationEntitlement,
  SubscriptionScheduleAction,
} from '@css-xp/graphtypes/src/generated-types-new';
import { EntitlementBaseProduct } from '@atlassian/cssxp-types-entitlement';
import { PartnerType } from '@atlassian/cssxp-typescommon';
import { isDeletedEntitlement, isFreePlanEntitlement, isOfferingAtNotice } from '@atlassian/cssxp-utils-entitlement';
import {
  ENABLE_ANNUAL_TERMS,
  ENABLE_CREDIT_MANAGEMENT,
  ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE,
  ENABLE_MARK_FAVORITE,
  ENABLE_PRODUCT_DATA_DELETION,
  ENABLE_QUOTE,
  ENABLE_REACTIVATION_FOR_DELETED_ENTITLEMENT,
  ENABLE_SANDBOX_ENTITLEMENT,
  ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS,
} from '@atlassian/cssxp-utils-generic';
import { isBilledElsewhereProduct, isJCAProduct, isStatuspage } from '@atlassian/cssxp-utils-product';
import { ApprovalType, useApprovalDeskPermissions } from '@css-xp/apd-common';
import { useTaskList } from '@css-xp/apd-task-manager';
import {
  AppLozenge,
  CopyableText,
  MoreActionsItems,
  MoreActionsV2,
  OpenSidebarPayload,
  PaymentErrorLozenge,
  SandboxLozenge,
  StatusLozenge,
  useFeatureToggle,
  useModalDialog,
  useSidebar,
  useWarningModalDialog,
} from '@css-xp/base';
import { TransactionAccountContext } from '@css-xp/base/common-context';
import { EntitlementStatus } from '@css-xp/base/entitlement-utils';
import { defaultTo } from 'lodash';
import lowerCase from 'lodash/lowerCase';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { getUndoScheduleChangePayload, validateSubscriptionDetails } from '../..';
import { BilledElsewhereBadge } from '../../../common/components/billed-elsewhere-badge';
import { PREDICATES } from '../../../common/constants';
import { getPredicates } from '../../../common/utils';
import { isTrialEntitlement } from '../../../entitlement-page-utility/entitlement-summary/entitlment-summary-utils';
import { SIDEBAR } from './entitlement-page';
import {
  CheckPaymentIssue,
  fetchWarningProps,
  getChangeBillDateSidebarPayload,
  getSidebarProps,
  getSwitchBillingProfilePayload,
  getSwitchTxaPayload,
  isActiveEntitlementWithoutScheduleCancellation,
  StatuspageSubheaderText,
  validateFreeSubscriptions,
} from './entitlement-page-utils';
import Button from '@atlaskit/button';
import { useFavorites } from '@css-xp/base/hooks/src/use-favourites';
import { PAGETITLE } from '@atlassian/temp-analytics';
// eslint-disable-next-line
import { FavouriteButton } from '../../../../search-v2/favourites';

interface StatuspageHeaderActionsProps {
  entitlement: EntitlementDetails;
  refetch: () => void;
  parentProductEntitlements: LinkedDestinationEntitlement[];
}

const StatuspageHeaderActions = ({ entitlement, refetch, parentProductEntitlements }: StatuspageHeaderActionsProps) => {
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const { billingStatus, baseProduct, billingFrequency } = entitlement;
  const { openSidebar } = useSidebar();
  const [isEnabled] = useFeatureToggle();
  const history = useHistory();
  const handleSidebarAction = (openSidebarPayload: OpenSidebarPayload) => {
    openSidebar(openSidebarPayload);
  };

  const isCreditManagementEnabled = isEnabled(ENABLE_CREDIT_MANAGEMENT);
  const isSandboxEntEnable = isEnabled(ENABLE_SANDBOX_ENTITLEMENT);
  const isEntRelationFeatureEnable = isEnabled(ENABLE_ENTITLEMENT_RELATIONSHIP_FEATURE);
  const isWarningModalDialogEnable = isEnabled(ENABLE_WARNING_MODAL_DAILOG_RELATED_ENTS);
  const enableReactivationForDeletedEntitlement = isEnabled(ENABLE_REACTIVATION_FOR_DELETED_ENTITLEMENT);
  const isAnnualTermEnabled = isEnabled(ENABLE_ANNUAL_TERMS);
  const { isFavorited, handleFavoriteToggle } = useFavorites();
  const isMarkFavoriteEnabled = isEnabled(ENABLE_MARK_FAVORITE);

  const { openModalDialog } = useModalDialog();
  const { openWarningModalDialog, closeWarningModalDialog } = useWarningModalDialog();

  const handleConfirmWarningDailog = (sidebarPayload: OpenSidebarPayload) => {
    closeWarningModalDialog();
    openSidebar(sidebarPayload);
  };

  const linkedEntsTableVisible =
    isEntRelationFeatureEnable &&
    (!!entitlement?.relatesFromEntitlements?.length || !!entitlement?.relatesToEntitlements?.length);

  const warningModalDailogVisible = linkedEntsTableVisible && isWarningModalDialogEnable;

  const isActive = lowerCase(billingStatus ?? '') === lowerCase(BillingStatus.ACTIVE);
  const isInactive = lowerCase(billingStatus ?? '') === lowerCase(BillingStatus.INACTIVE);
  const hasPromotions = !!(
    ((entitlement.promotions ?? []).length || (entitlement.scheduledChanges?.promotions ?? [])?.length) > 0
  );
  const isStatusPage = baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isOnUpgradeTrial = !!entitlement.upgradeTrial;
  const isTrial = isTrialEntitlement(entitlement as EntitlementDetails, isStatusPage);
  const isAnnual = lowerCase(billingFrequency ?? '') === lowerCase(BillingFrequency.ANNUAL);

  const { isTrialExtApprovalFlowEnabled } = useApprovalDeskPermissions();

  const { data, getTransactionAccount } = transactionAccountContext;
  const txaccount = getTransactionAccount(data);

  const availableInvoiceGroups = defaultTo(txaccount?.availableInvoiceGroups?.nodes, []) as InvoiceGroup[];

  const activeBillingGroupId =
    (entitlement as EntitlementDetails)?.activeSubscriptionBillingGroupId ?? entitlement.billingGroupId;

  let invoiceGroup: any = null;
  if (!activeBillingGroupId) {
    invoiceGroup = null;
  } else {
    invoiceGroup = txaccount?.availableInvoiceGroups?.nodes.find(
      (invoiceGroup) => activeBillingGroupId === invoiceGroup?.id
    );
  }

  const { loading: taskListLoading, error: taskListError, hasOpenApprovalRequest } = useTaskList({
    businessKey: entitlement?.entitlementNumber,
    skip: !isTrialExtApprovalFlowEnabled || !isTrial,
    approvalType: ApprovalType.TRIAL_EXTENSION,
  });

  const cancelActiveEntProps = getSidebarProps(
    SIDEBAR.CANCEL_ACTIVE_SUBSCRIPTION,
    {
      entitlementNumber: entitlement?.entitlementNumber,
      entitlementId: entitlement.id,
      subscriptionStatus: isActive,
      entitlement,
      transactionAccountContext,
      refetch,
    },
    '600px'
  );

  let changeBillDatePayload = getChangeBillDateSidebarPayload(entitlement, refetch);
  changeBillDatePayload = {
    ...changeBillDatePayload,
    payload: {
      ...changeBillDatePayload.payload,
      billingFrequency: entitlement.billingFrequency,
    },
  };
  const switchBillingProfilePayload = getSwitchBillingProfilePayload(entitlement, transactionAccountContext, refetch);
  const switchTxaPayload = getSwitchTxaPayload({ entitlement, transactionAccount: txaccount, invoiceGroup, refetch });
  const undoScheduledChangesPayload = getUndoScheduleChangePayload(entitlement, refetch);
  let isAppAtNotice = false;
  let isUndoScheduleEnabled = true;
  if (entitlement?.isApp) {
    isAppAtNotice = isOfferingAtNotice(entitlement);
    isUndoScheduleEnabled =
      !(entitlement?.scheduledChanges?.subscriptionScheduleAction === SubscriptionScheduleAction.CANCEL) ||
      isActiveEntitlementWithoutScheduleCancellation(parentProductEntitlements);
  }
  const getEntityNameAndMetadata = () => {
    const entityName = PAGETITLE.SUBSCRIPTION.toLowerCase().trim().replace(/\s+/g, '_');
    const metadata = {
      productId: entitlement.productId,
      productName: entitlement.productName,
      cloudSiteHostname: entitlement.cloudSiteHostname,
    };
    return { entityName, metadata };
  };

  const statuspageActions: MoreActionsItems[] = [
    {
      id: PREDICATES.MANAGE_SUBSCRIPTION,
      content: 'Change subscription',
      action: () =>
        validateSubscriptionDetails(entitlement, availableInvoiceGroups, txaccount)
          ? handleSidebarAction({
              sidebarType: SIDEBAR.STATUSPAGE_CHANGE_PLAN,
              payload: {
                entitlementDetails: entitlement,
                transactionAccountContext,
                refetch,
              },
              sidebarOptions: {
                isOverlay: true,
                sidebarWidth: '600px',
              },
            })
          : openModalDialog(),
      predicate: getPredicates(entitlement, PREDICATES.MANAGE_SUBSCRIPTION, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.CANCEL_OR_REACTIVATE_ENTITLEMENT,
      content: isActive ? 'Cancel subscription' : 'Reactivate subscription',
      action: () =>
        warningModalDailogVisible && isActive
          ? openWarningModalDialog({
              ...fetchWarningProps(PREDICATES.CANCEL_OR_REACTIVATE_ENTITLEMENT),
              onConfirm: () =>
                handleConfirmWarningDailog({ ...cancelActiveEntProps, sidebarType: cancelActiveEntProps.sidebar }),
            })
          : handleSidebarAction({ ...cancelActiveEntProps, sidebarType: cancelActiveEntProps.sidebar }),
      predicate: getPredicates(entitlement, PREDICATES.CANCEL_OR_REACTIVATE_ENTITLEMENT, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.DELETE_PRODUCT,
      content: 'Delete product data',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.DELETE_PRODUCT_DATA,
          payload: {
            entitlementNumber: entitlement?.entitlementNumber,
            entitlementId: entitlement.id,
            subscriptionStatus: isActive,
            entitlement,
            transactionAccountContext,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate:
        getPredicates(entitlement, PREDICATES.DELETE_PRODUCT, { isSandboxEntEnable }) &&
        isEnabled(ENABLE_PRODUCT_DATA_DELETION),
    },
    {
      id: PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION,
      content: 'Undo scheduled changes',
      isDisabled: !isUndoScheduleEnabled,
      action: () =>
        warningModalDailogVisible
          ? openWarningModalDialog({
              ...fetchWarningProps(PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION),
              onConfirm: () =>
                handleConfirmWarningDailog({
                  ...undoScheduledChangesPayload,
                  sidebarType: undoScheduledChangesPayload.sidebar,
                }),
            })
          : handleSidebarAction({ ...undoScheduledChangesPayload, sidebarType: undoScheduledChangesPayload.sidebar }),
      predicate: getPredicates(entitlement, PREDICATES.CANCEL_SCHEDULED_SUBSCRIPTION, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.OPT_OUT,
      content: 'Opt out',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.OPTOUT_TRIAL,
          payload: {
            entitlementNumber: entitlement?.entitlementNumber,
            entitlementId: entitlement.id,
            subscriptionStatus: isActive,
            entitlement,
            transactionAccountContext,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.OPT_OUT, { isSandboxEntEnable }),
    },
    {
      id: PREDICATES.SET_BILL_DATE,
      isDisabled: isAppAtNotice,
      content: 'Change bill date',
      action: () =>
        warningModalDailogVisible
          ? openWarningModalDialog({
              ...fetchWarningProps(PREDICATES.SET_BILL_DATE),
              onConfirm: () =>
                handleConfirmWarningDailog({
                  ...changeBillDatePayload,
                  sidebarType: changeBillDatePayload.sidebar,
                }),
            })
          : handleSidebarAction({
              ...changeBillDatePayload,
              sidebarType: changeBillDatePayload.sidebar,
            }),
      predicate: getPredicates(entitlement, PREDICATES.SET_BILL_DATE, {
        isSandboxEntEnable,
        isAnnualTermEnabled,
      }),
    },
    {
      id: PREDICATES.SWITCH_TO_ANNUAL,
      content: 'Switch to annual billing',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.STATUSPAGE_SWITCH_TO_ANNUAL_SUBSCRIPTION,
          payload: {
            entitlement,
            refetch,
            transactionAccountContext,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate:
        isStatuspage(entitlement?.productId) &&
        getPredicates(entitlement, PREDICATES.SWITCH_TO_ANNUAL, { isSandboxEntEnable }),
    },
    {
      id: PREDICATES.SWITCH_TO_MONTHLY,
      content: 'Switch to monthly billing',
      isDisabled: isAnnual && !isStatuspage(entitlement?.productId),
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.STATUSPAGE_SWITCH_TO_MONTHLY_SUBSCRIPTION,
          payload: {
            entitlement,
            refetch,
            transactionAccountContext,
          },
          sidebarOptions: {
            isOverlay: false,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.SWITCH_TO_MONTHLY, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.UPDATE_INVOICE_GROUP,
      content: 'Switch billing profile',
      isDisabled: !!entitlement?.hasPendingInvoices,
      action: () =>
        warningModalDailogVisible
          ? openWarningModalDialog({
              ...fetchWarningProps(PREDICATES.UPDATE_INVOICE_GROUP),
              onConfirm: () =>
                handleConfirmWarningDailog({
                  ...switchBillingProfilePayload,
                  sidebarType: switchBillingProfilePayload.sidebar,
                }),
            })
          : handleSidebarAction({ ...switchBillingProfilePayload, sidebarType: switchBillingProfilePayload.sidebar }),
      predicate:
        getPredicates(entitlement, PREDICATES.UPDATE_INVOICE_GROUP, {
          isSandboxEntEnable,
        }) && !validateFreeSubscriptions(entitlement as EntitlementDetails),
    },
    {
      id: PREDICATES.TRANSFER_BILLING_ACCOUNT,
      content: 'Transfer billing account',
      isDisabled: !!entitlement?.hasPendingInvoices || (isCreditManagementEnabled && entitlement?.hasOpenInvoices),
      action: () =>
        warningModalDailogVisible
          ? openWarningModalDialog({
              ...fetchWarningProps(PREDICATES.TRANSFER_BILLING_ACCOUNT),
              onConfirm: () =>
                handleConfirmWarningDailog({
                  ...switchTxaPayload,
                  sidebarType: switchTxaPayload.sidebar,
                }),
            })
          : handleSidebarAction({ ...switchTxaPayload, sidebarType: switchTxaPayload.sidebar }),
      predicate:
        getPredicates(entitlement, PREDICATES.TRANSFER_BILLING_ACCOUNT, {
          isSandboxEntEnable,
        }) && !validateFreeSubscriptions(entitlement as EntitlementDetails),
    },
    {
      id: PREDICATES.REWIND_SUBSCRIPTION,
      content: 'Rewind',
      isDisabled: isJCAProduct(entitlement.productId),
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.REWIND_AND_REBILL_SUBSCRIPTION,
          payload: {
            id: entitlement?.id,
            refetch,
            transactionAccountContext,
            promotions: entitlement?.availablePromotions,
            appliedPromotions:
              entitlement?.scheduledChanges?.promotions && entitlement?.scheduledChanges?.promotions.length > 0
                ? entitlement?.scheduledChanges?.promotions
                : entitlement?.promotions,
            billingGroupIdOnEntitlement: entitlement?.billingGroupId ?? '',
            billingFrequencyOnEntitlement: entitlement?.billingFrequency ?? '',
            entitlement,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.REWIND_SUBSCRIPTION, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.REBILL,
      content: 'Rebill',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.REBILL_SUBSCRIPTION,
          payload: {
            entitlementNumber: entitlement?.entitlementNumber,
            entitlementId: entitlement.id,
            subscriptionStatus: isActive,
            entitlement,
            transactionAccountContext,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.REBILL, { isSandboxEntEnable }),
    },
    // {
    //   id: PREDICATES.CHANGE_PRICING,
    //   content: 'Change pricing',
    //   action: () =>
    //     handleSidebarAction({
    //       sidebarType: SIDEBAR.CHANGE_PRICING_PLAN,
    //       payload: {
    //         entitlement,
    //         transactionAccountContext,
    //         refetch,
    //       },
    //       sidebarOptions: {
    //         isOverlay: true,
    //         sidebarWidth: '600px',
    //       },
    //     }),
    //   predicate: getPredicates(entitlement, PREDICATES.CHANGE_PRICING),
    // },
    {
      id: PREDICATES.ADD_PROMOTIONS,
      content: 'Add discounts',
      isDisabled: isOnUpgradeTrial,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.APPLY_PROMOTION,
          payload: {
            entitlement,
            transactionAccountContext,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.ADD_PROMOTIONS, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.REMOVE_PROMOTION,
      content: 'Remove discounts',
      isDisabled: !hasPromotions || isOnUpgradeTrial,
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.REMOVE_PROMOTION,
          payload: {
            entitlement,
            transactionAccountContext,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.REMOVE_PROMOTION, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.EXTEND_TRIAL,
      content: 'Extend trial',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.EXTEND_TRIAL,
          payload: {
            entitlement,
            refetch,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate:
        getPredicates(entitlement, PREDICATES.EXTEND_TRIAL, { isSandboxEntEnable }) &&
        !(taskListLoading || taskListError || hasOpenApprovalRequest),
    },
    {
      id: PREDICATES.SUBSCRIBE_TO_ADD_ONS,
      content: 'Subscribe to additional allocations',
      action: () =>
        handleSidebarAction({
          sidebarType: SIDEBAR.SUBSCRIBE_TO_ADD_ONS,
          payload: {
            entitlement,
            availableAddOns: entitlement.offering?.availableAddOns,
            refetch,
            transactionAccountContext,
          },
          sidebarOptions: {
            isOverlay: true,
            sidebarWidth: '600px',
          },
        }),
      predicate: getPredicates(entitlement, PREDICATES.SUBSCRIBE_TO_ADD_ONS, {
        isSandboxEntEnable,
      }),
    },
    {
      id: PREDICATES.CREATE_QUOTE,
      content: 'Create quote',
      action: () =>
        history.push(
          `/quotes/new?txId=${entitlement.transactionAccountId}&entitlementid=${entitlement.entitlementNumber}`
        ),
      predicate:
        isEnabled(ENABLE_QUOTE) &&
        getPredicates(entitlement, PREDICATES.CREATE_QUOTE, {
          isSandboxEntEnable,
        }),
    },
  ];

  if (
    (isSandboxEntEnable && entitlement?.isSandbox && isInactive) ||
    isDeletedEntitlement(entitlement, enableReactivationForDeletedEntitlement) ||
    isBilledElsewhereProduct(entitlement?.productId)
  ) {
    return null;
  }

  return (
    <div className="button-toolbar" data-testid="header-actions">
      {isMarkFavoriteEnabled && (
        <FavouriteButton
          isFavorited={isFavorited}
          {...getEntityNameAndMetadata()}
          handleFavoriteToggle={handleFavoriteToggle}
          data-testId="favourite"
        />
      )}
      <MoreActionsV2 actions={statuspageActions} trigger="Manage" />
    </div>
  );
};

const partnerLozenge = (partnerType: PartnerType): JSX.Element => (
  <span className="page-header-lozenge">
    <StatusLozenge status={'whitelisted'} label={`${partnerType.toUpperCase()} PARTNER`} />
  </span>
);

const StatuspageHeader = ({ entitlement }: { entitlement: EntitlementDetails }) => {
  const [isEnabled] = useFeatureToggle();
  const { billingStatus, provisioningStatus, scheduledChanges, edition, isSandbox, isApp, productId } = entitlement;
  const transactionAccountContext = React.useContext(TransactionAccountContext);
  const transactionAccount = transactionAccountContext?.data?.entitlementDetails?.transactionAccount;
  const header = entitlement.productName + ' ' + entitlement.entitlementNumber;
  const isStatusPage = entitlement.baseProduct === EntitlementBaseProduct.STATUS_PAGE;
  const isFreePlan = edition ? edition.includes('Free') : false;
  const billedElsewhere = isBilledElsewhereProduct(productId);
  const isTrial = !isFreePlan && isTrialEntitlement(entitlement, isStatusPage);
  const isOnUpgradeTrial = !!entitlement?.upgradeTrial;

  return (
    <>
      <div className="page-header-text">
        <CopyableText attribute="data-entitlement-number" duration={0}>
          <span data-entitlement-number={entitlement.entitlementNumber}>{header}</span>
        </CopyableText>
      </div>
      {transactionAccount?.partnerLevel && partnerLozenge(transactionAccount.partnerLevel)}
      <EntitlementStatus
        billingStatus={billingStatus}
        provisioningStatus={provisioningStatus}
        scheduledChanges={scheduledChanges}
        entitlement={entitlement}
        trial={isTrial}
        upgradeTrial={isOnUpgradeTrial}
      />
      {entitlement?.invoices?.nodes &&
        entitlement?.invoices?.nodes?.length > 0 &&
        CheckPaymentIssue(entitlement, transactionAccount) &&
        !isFreePlanEntitlement(entitlement) && (
          <span className="page-header-lozenge">
            <PaymentErrorLozenge />
          </span>
        )}
      {isEnabled(ENABLE_SANDBOX_ENTITLEMENT) && isSandbox && <SandboxLozenge />}
      {isApp && <AppLozenge />}
      {billedElsewhere && <BilledElsewhereBadge className="billed-elsewhere" />}
    </>
  );
};

const StatuspageSubheader = ({ entitlement }: { entitlement: EntitlementDetails }) => (
  <StatuspageSubheaderText entitlement={entitlement} />
);

export { StatuspageHeaderActions, StatuspageHeader, StatuspageSubheader };
